import { LogOut } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { handleSignOut } from "@/lib/auth";
import { useStore } from "@/lib/store";

import { AnimatedThemeToggleIcon } from "./AnimatedThemeToggleButton";

interface SidebarFooterProps {
  collapsed?: boolean;
  className?: string;
}

export function SidebarFooter({
  collapsed,
  className,
}: SidebarFooterProps): React.JSX.Element | null {
  const user = useStore((state) => state.user);
  const theme = useStore((state) => state.theme);
  const toggleTheme = useStore((state) => state.toggleTheme);
  const [open, setOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number } | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!open) return;

    function handlePointerDown(event: PointerEvent): void {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    }

    function handleKeyDown(event: KeyboardEvent): void {
      if (event.key === "Escape") {
        setOpen(false);
      }
    }

    window.addEventListener("pointerdown", handlePointerDown);
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("pointerdown", handlePointerDown);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  useEffect(() => {
    if (!collapsed || !open) return;

    function updatePosition(): void {
      const button = buttonRef.current;
      if (!button) return;

      const rect = button.getBoundingClientRect();
      const width = 192;
      const padding = 8;
      const left = Math.max(
        padding,
        Math.min(rect.left + rect.width / 2 - width / 2, window.innerWidth - width - padding),
      );

      setMenuPosition({
        top: rect.top - 8,
        left,
      });
    }

    updatePosition();
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);

    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [collapsed, open]);

  if (!user) return null;

  const sidebarHoverSurfaceClass =
    theme === "base" ? "hover:bg-cyber-border/80" : "hover:bg-cyber-border/50";

  const avatar = user.image ? (
    <img src={user.image} alt="" className="h-8 w-8 rounded-full" />
  ) : (
    <div className="bg-cyber-surface text-cyber-muted flex h-8 w-8 items-center justify-center rounded-full text-xs">
      {user.email[0].toUpperCase()}
    </div>
  );

  if (collapsed) {
    return (
      <div ref={rootRef} className="flex justify-center pt-2">
        <button
          ref={buttonRef}
          type="button"
          onClick={() => setOpen((current) => !current)}
          className="hover:ring-cyber-border/80 relative rounded-full transition-all before:absolute before:-inset-1 before:rounded-full before:content-[''] hover:ring-2"
          aria-label="Open user menu"
          aria-expanded={open}
          title={user.email}
        >
          {avatar}
        </button>

        {open && menuPosition ? (
          <div
            className="border-cyber-border bg-cyber-bg/95 fixed z-50 w-48 -translate-y-full overflow-hidden rounded-xl border shadow-[0_16px_32px_rgba(0,0,0,0.28)] backdrop-blur"
            style={{
              top: `${menuPosition.top}px`,
              left: `${menuPosition.left}px`,
            }}
          >
            <div className="text-cyber-muted px-3 py-2 text-[11px]">{user.email}</div>
            <button
              onClick={handleSignOut}
              className={[
                "flex w-full items-center gap-2 px-3 py-2 text-left text-[12px] text-cyber-muted transition-all hover:text-cyber-red",
                sidebarHoverSurfaceClass,
              ].join(" ")}
              title="Sign out"
              aria-label="Sign out"
            >
              <LogOut size={14} />
              <span>Log out</span>
            </button>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div ref={rootRef} className={["px-3 py-3", className].filter(Boolean).join(" ")}>
      <div className="flex flex-col gap-2">
        {open ? (
          <div
            className="border-cyber-border bg-cyber-bg/95 overflow-hidden rounded-xl border transition-all"
            aria-hidden={!open}
          >
            <div className="text-cyber-muted px-3 py-2 text-[11px]">{user.email}</div>
            <button
              onClick={handleSignOut}
              className={[
                "flex w-full items-center gap-2 px-3 py-2 text-left text-[12px] text-cyber-muted transition-all hover:text-cyber-red",
                sidebarHoverSurfaceClass,
              ].join(" ")}
              title="Sign out"
              aria-label="Sign out"
            >
              <LogOut size={14} />
              <span>Log out</span>
            </button>
          </div>
        ) : null}

        <div className="flex items-center justify-between">
          <button
            ref={buttonRef}
            type="button"
            onClick={() => setOpen((current) => !current)}
            className="hover:ring-cyber-border/80 relative rounded-full transition-all before:absolute before:-inset-1 before:rounded-full before:content-[''] hover:ring-2"
            aria-label="Open user menu"
            aria-expanded={open}
            title={user.email}
          >
            {avatar}
          </button>

          <button
            type="button"
            onClick={toggleTheme}
            className="text-cyber-muted hover:bg-cyber-border/50 hover:text-cyber-text inline-flex items-center gap-2 rounded-full px-2 py-1 text-[12px] transition-all"
            aria-label={theme === "cyber" ? "Switch to light theme" : "Switch to cyber theme"}
            title={theme === "cyber" ? "Switch to light theme" : "Switch to cyber theme"}
          >
            <span>{theme === "cyber" ? "Switch to Light" : "Switch to Cyber"}</span>
            <AnimatedThemeToggleIcon
              checked={theme === "cyber"}
              hovered={false}
              className="size-4 shrink-0"
            />
          </button>
        </div>
      </div>
    </div>
  );
}
